/**
 * W-07 "Hareket defteri" + "Yeni stok hareketi" — the pure half of both: which
 * reasons a movement kind may carry, which fields that kind shows, what the
 * entered quantity converts to, what the balances will read after posting, and
 * how a ledger row prints its quantity and its date range.
 *
 * Every rule is a mirror of the posting engine (migration 20260923000003):
 * `private.movement_before` for the shapes and the reason gates,
 * `private.movement_apply` for the negative guard (C31: a lot balance never goes
 * negative, even in a warehouse that allows it), `private.reverse_movement` for
 * the undo window. The form has to be able to say "no" before a row is sent — a
 * refusal that only arrives from the server costs the user the whole entry — and
 * the server stays the authority either way.
 *
 * Quantities travel in base units and are shown in the *entered* unit, which is
 * what the artboard's "Sonuç önizleme" prints ("420 → 300 koli").
 */
import { D, type Dec, type DecimalInput } from '../money';
import { formatDateShort, formatNumber, ISTANBUL_OFFSET_MIN, istanbulParts, monthStart, toIsoDate, type Locale } from '../format';
import type { MemberRole, StockMovementKind, StockMovementReason } from '../constants';
import { itemUnitFactor, type ItemUomContext } from './uom';

// ── kinds and signs ─────────────────────────────────────────────────────────
/** Giriş · Çıkış · Transfer · Düzeltme — the segmented control's order (§4.5.2). */
export const STOCK_MOVEMENT_KIND_ORDER: StockMovementKind[] = ['in', 'out', 'transfer', 'adjust'];

export interface MovementSides {
  fromLocationId?: string | null;
  toLocationId?: string | null;
}

/**
 * `movementSign()` (§4.4): `in` +, `out` −, `transfer` none, `adjust` + when the
 * quantity lands somewhere and − when it leaves. Returns 0 for a transfer, whose
 * quantity is drawn without a sign because nothing is created or destroyed.
 */
export function movementSign(kind: StockMovementKind, sides: MovementSides = {}): -1 | 0 | 1 {
  if (kind === 'transfer') return 0;
  if (kind === 'in') return 1;
  if (kind === 'out') return -1;
  return sides.toLocationId ? 1 : -1;
}

/** Ledger precision: at most one decimal, and never more than the unit has (§1.2). */
export function movementQtyPrecision(unitPrecision: number | null | undefined): number {
  return Math.min(unitPrecision ?? 0, 1);
}

/**
 * How many decimals the ledger always writes, even on a round number. A *measured*
 * unit (kg, m — `units.precision` 3) keeps its one decimal, which is why the
 * artboard prints "−40,0 kg" next to "−84,0 m": in a column of weights, 40 and
 * 40,4 have to line up. A counted unit (adet, paket, koli) prints a decimal only
 * when the quantity has one, hence "12 koli" and "+4.800 adet".
 */
export function movementQtyMinDecimals(unitPrecision: number | null | undefined): number {
  return (unitPrecision ?? 0) >= 2 ? movementQtyPrecision(unitPrecision) : 0;
}

/**
 * The "Miktar" cell: "+4.800 adet", "−40,0 kg", "12 koli". A transfer prints the
 * amount unsigned; everything else carries its sign, and the plus is explicit so
 * a receipt cannot be mistaken for an issue. Positive is *not* green (§4.4).
 */
export function formatMovementQty(
  signedQty: DecimalInput,
  unitLabel: string,
  opts: { precision?: number; kind?: StockMovementKind; locale?: Locale } = {},
): string {
  const v = D(signedQty);
  const body = formatNumber(opts.kind === 'transfer' ? v.abs() : v, {
    decimals: movementQtyPrecision(opts.precision ?? 0),
    minDecimals: movementQtyMinDecimals(opts.precision ?? 0),
    locale: opts.locale,
  });
  const plus = opts.kind !== 'transfer' && v.greaterThan(0) ? '+' : '';
  return `${plus}${body} ${unitLabel}`.trim();
}

// ── date presets (§4.3) ─────────────────────────────────────────────────────
export const LEDGER_PRESETS = ['today', 'yesterday', '7d', 'month', 'all', 'custom'] as const;
export type LedgerPreset = (typeof LEDGER_PRESETS)[number];

export interface LedgerRange {
  /** Istanbul day start, inclusive (ISO with the +03:00 offset); absent = open. */
  fromTs?: string;
  /** Istanbul day end, exclusive. */
  toTs?: string;
  /** The ISO calendar days the chip shows, for the custom inputs. */
  fromDate?: string;
  toDate?: string;
}

const pad = (n: number) => String(n).padStart(2, '0');
const OFFSET = `+${pad(ISTANBUL_OFFSET_MIN / 60)}:${pad(ISTANBUL_OFFSET_MIN % 60)}`;

/** "2026-09-24" → "2026-09-24T00:00:00+03:00" — a day boundary the server reads as ours. */
export function istanbulDayStart(isoDate: string): string {
  return `${isoDate}T00:00:00${OFFSET}`;
}

function addIsoDays(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}

/**
 * Preset → the half-open Istanbul range the ledger query filters on. "Bu ay" is
 * the current calendar month to today, "Son 7 gün" is today and the six days
 * before it (the way the design's "12–21 Eyl" custom chip counts inclusive days).
 */
export function ledgerRange(preset: LedgerPreset, custom: { from?: string; to?: string } = {}, now: Date | string | number = new Date()): LedgerRange {
  const today = toIsoDate(now);
  const range = (from: string, to: string): LedgerRange => ({
    fromTs: istanbulDayStart(from),
    toTs: istanbulDayStart(addIsoDays(to, 1)),
    fromDate: from,
    toDate: to,
  });
  switch (preset) {
    case 'today':
      return range(today, today);
    case 'yesterday':
      return range(addIsoDays(today, -1), addIsoDays(today, -1));
    case '7d':
      return range(addIsoDays(today, -6), today);
    case 'month':
      return range(monthStart(now), today);
    case 'custom': {
      if (!custom.from && !custom.to) return {};
      const from = custom.from ?? custom.to!;
      const to = custom.to ?? custom.from!;
      return from <= to ? range(from, to) : range(to, from);
    }
    case 'all':
    default:
      return {};
  }
}

/**
 * The custom chip's label: "12–21 Eyl" inside one month, "28 Ağu – 03 Eyl" across
 * two. The month name comes from `formatDateShort`, so the ledger and every other
 * screen spell Ağustos the same way.
 */
export function ledgerRangeLabel(range: LedgerRange, locale: Locale = 'tr'): string | null {
  if (!range.fromDate || !range.toDate) return null;
  const a = istanbulParts(range.fromDate);
  const b = istanbulParts(range.toDate);
  const sameMonth = a.year === b.year && a.month === b.month;
  const end = formatDateShort(range.toDate, locale, range.toDate);
  return sameMonth ? `${pad(a.day)}–${end}` : `${formatDateShort(range.fromDate, locale, range.fromDate)} – ${end}`;
}

// ── virtual endpoints (§4.4) ────────────────────────────────────────────────
/**
 * The side of a movement that has no location: "Mal kabul → D1-H-02",
 * "D1-H-01 → Fire". `v_stock_ledger` builds the same labels in SQL for the rows
 * that are already posted; the form needs them *before* posting, so this returns
 * the i18n suffix (`stock.route.*`) instead of a Turkish string — the ledger row
 * keeps the view's label, the form translates.
 */
export type RouteEndpointKey = 'receipt' | 'production' | 'scrap' | 'sale' | 'sample' | 'return' | 'opening' | 'count' | 'adjustment' | 'issue';

export function virtualEndpointKey(reason: StockMovementReason | null | undefined, side: 'from' | 'to'): RouteEndpointKey {
  if (side === 'from') {
    switch (reason) {
      case 'receipt':
        return 'receipt';
      case 'production_output':
        return 'production';
      case 'opening':
        return 'opening';
      case 'return':
        return 'return';
      case 'count':
        return 'count';
      default:
        return 'adjustment';
    }
  }
  switch (reason) {
    case 'scrap':
      return 'scrap';
    case 'production_consume':
      return 'production';
    case 'sale':
      return 'sale';
    case 'sample':
      return 'sample';
    case 'return':
      return 'return';
    case 'count':
      return 'count';
    case 'adjustment':
      return 'adjustment';
    default:
      return 'issue';
  }
}

// ── reason codes (§4.5.3, C21) ─────────────────────────────────────────────
/** A `stock_reason_codes` row as the form reads it; labels come from the database. */
export interface ReasonCode {
  id: string;
  code: string;
  label_tr: string;
  label_en?: string | null;
  reason: StockMovementReason;
  kinds?: StockMovementKind[] | null;
  is_manual?: boolean | null;
  requires_note?: boolean | null;
  requires_photo?: boolean | null;
  allowed_roles?: MemberRole[] | null;
  sort?: number | null;
  is_active?: boolean | null;
}

export function reasonLabel(code: ReasonCode, locale: Locale = 'tr'): string {
  return (locale === 'en' ? code.label_en || code.label_tr : code.label_tr) ?? code.code;
}

/**
 * The chips a kind offers: manual, active, allowed for the role, and — for
 * Düzeltme — only the reasons that may move the quantity in the chosen
 * direction. `movement_before` refuses a write-off with a target location
 * ("Düzeltme +" may only be `adjustment` or a count), so the form must not offer
 * "Fire · imha" as a plus.
 */
export function manualReasonsFor(
  codes: ReasonCode[],
  kind: StockMovementKind,
  role: MemberRole | null | undefined,
  opts: { adjustDirection?: 'in' | 'out' } = {},
): ReasonCode[] {
  return codes
    .filter((c) => c.is_manual !== false && c.is_active !== false)
    .filter((c) => (c.kinds ?? []).includes(kind))
    .filter((c) => !c.allowed_roles || (!!role && c.allowed_roles.includes(role)))
    .filter((c) => !(kind === 'adjust' && opts.adjustDirection === 'in' && c.reason !== 'adjustment' && c.reason !== 'count'))
    .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0) || a.code.localeCompare(b.code, 'tr'));
}

// ── field visibility per kind (§4.5.3) ─────────────────────────────────────
export type EndpointMode =
  /** A location picker the user fills. */
  | 'picker'
  /** A read-only box showing the virtual source/sink of the chosen reason. */
  | 'virtual'
  /** Not part of this kind at all. */
  | 'hidden';

export interface MovementFieldLayout {
  source: EndpointMode;
  target: EndpointMode;
  /** Düzeltme is the only kind that asks for a direction. */
  signToggle: boolean;
  /** Giriş and Düzeltme(+) may create a lot; the others pick an existing one. */
  allowNewLot: boolean;
  /** The source and target share one row ("→") unless Düzeltme collapses it. */
  singleLocation: boolean;
}

/**
 * Which endpoints a kind draws. "FBA sevkiyat" is the exception the engine
 * itself makes: an `out` whose target is a real FBA location, so the artboard's
 * read-only sink becomes a picker for that one reason.
 *
 * `adjustDirection` decides whether Düzeltme may create a lot: `private.post_movement`
 * reads a `new_lot` only on an inbound side (`to` set, `from` empty), so a minus
 * carrying one is refused with "lot seçilmeli". Without a direction the minus —
 * the form's own default — is assumed, and "+ Yeni lot" stays hidden.
 */
export function movementFieldLayout(
  kind: StockMovementKind,
  reason?: StockMovementReason | null,
  opts: { adjustDirection?: 'in' | 'out' } = {},
): MovementFieldLayout {
  switch (kind) {
    case 'in':
      return { source: 'virtual', target: 'picker', signToggle: false, allowNewLot: true, singleLocation: false };
    case 'out':
      return {
        source: 'picker',
        target: reason === 'fba_inbound' ? 'picker' : 'virtual',
        signToggle: false,
        allowNewLot: false,
        singleLocation: false,
      };
    case 'transfer':
      return { source: 'picker', target: 'picker', signToggle: false, allowNewLot: false, singleLocation: false };
    case 'adjust':
    default:
      return { source: 'picker', target: 'hidden', signToggle: true, allowNewLot: opts.adjustDirection === 'in', singleLocation: true };
  }
}

/** A kind change keeps what still applies and drops what cannot travel (§4.5.3). */
export function reasonSurvivesKind(reason: ReasonCode | null | undefined, kind: StockMovementKind, role?: MemberRole | null): boolean {
  if (!reason) return false;
  return manualReasonsFor([reason], kind, role ?? null).length > 0;
}

// ── unit conversion hint (§4.5.2, M-04) ────────────────────────────────────
export interface QtyConversion {
  unit: string;
  qty: Dec;
}

/** At most two conversions: M-04 prints "= 96.000 adet · 1.920 paket" and stops. */
const HINT_UNITS = 2;

/**
 * What 120 koli also is: "= 96.000 adet · 1.920 paket". The hint lists the
 * item's *finer* units (smaller factor) from the finest up, because that is how
 * the shop floor checks an entry — a koli entry is verified in adet. An entry in
 * the finest unit has nothing below it, so it is shown in the stock unit
 * instead ("120 adet = 0,15 koli").
 *
 * A napkin also carries a kg row (weight per case), which is a conversion nobody
 * enters or checks a picking list against, so the list stops at two.
 */
export function conversionParts(qty: DecimalInput, unit: string | null | undefined, ctx: ItemUomContext & { stockUnit?: string | null }): QtyConversion[] {
  const entered = unit ?? ctx.baseUnit;
  const factor = itemUnitFactor(ctx, entered);
  const amount = D(qty);
  if (!factor || amount.isZero()) return [];
  const base = amount.times(factor);

  const all = [{ unit: ctx.baseUnit, factor: D(1) }, ...ctx.uoms.map((u) => ({ unit: u.unit, factor: D(u.factor_to_base ?? 0) }))]
    .filter((u, i, list) => u.factor.greaterThan(0) && list.findIndex((x) => x.unit === u.unit) === i)
    .filter((u) => u.unit !== entered);

  const finer = all.filter((u) => u.factor.lessThan(factor)).sort((a, b) => a.factor.comparedTo(b.factor));
  const chosen = finer.length
    ? finer.slice(0, HINT_UNITS)
    : all.filter((u) => u.unit === (ctx.stockUnit ?? '') && u.factor.greaterThan(factor)).slice(0, 1);
  return chosen.map((u) => ({ unit: u.unit, qty: base.dividedBy(u.factor) }));
}

/** The one line under "Miktar": "= 96.000 adet · 1.920 paket" (null when there is nothing to add). */
export function conversionHint(parts: QtyConversion[], unitInfo: (unit: string) => { label: string; precision?: number | null }, locale: Locale = 'tr'): string | null {
  if (!parts.length) return null;
  const body = parts
    .map((p) => {
      const info = unitInfo(p.unit);
      const decimals = p.qty.isInteger() ? 0 : Math.min(info.precision ?? 0, 2);
      return `${formatNumber(p.qty, { decimals, minDecimals: 0, locale })} ${info.label}`.trim();
    })
    .join(' · ');
  return `= ${body}`;
}

// ── result preview (§4.5.4) ────────────────────────────────────────────────
/** A balance row the preview reads: `v_stock_lots` (lot × location) or `v_item_stock_by_location`. */
export interface MovementBalanceRow {
  location_id?: string | null;
  location_code?: string | null;
  lot_id?: string | null;
  qty_base?: number | string | null;
}

export interface MovementPreviewInput {
  kind: StockMovementKind;
  /** Base quantity of the entry; null while the form is incomplete. */
  qtyBase?: DecimalInput | null;
  /** `item_uoms.factor_to_base` of the entered unit — the preview prints that unit. */
  unitFactor?: DecimalInput | null;
  fromLocationId?: string | null;
  toLocationId?: string | null;
  lotId?: string | null;
  /** "+ Yeni lot": nothing exists yet, so every line starts at zero. */
  newLot?: boolean;
  lotNo?: string | null;
  balances?: MovementBalanceRow[];
  /** `v_stock_item_summary.total_qty_base` — the same number W-06 prints as "Toplam". */
  itemTotalBase?: DecimalInput | null;
  /** `warehouses.allow_negative_stock` of the source location's warehouse. */
  allowNegativeAtSource?: boolean;
  /** Code of that warehouse, for the message under the box. */
  sourceWarehouseCode?: string | null;
  /** The item's lot tracking — a lot balance may never go negative (C31). */
  isLotTracked?: boolean;
}

export interface MovementPreviewLine {
  locationId: string;
  locationCode: string | null;
  /** The lot the line is about: a lot number, `null` for an untracked item, `'new'` for a lot being created. */
  lot: string | null | 'new';
  beforeBase: Dec;
  afterBase: Dec;
  /** Base → the entered unit. */
  before: Dec;
  after: Dec;
  negative: boolean;
  direction: -1 | 1;
}

export interface MovementPreview {
  /** False while item, quantity, lot or location are still missing. */
  complete: boolean;
  lines: MovementPreviewLine[];
  totalBeforeBase: Dec;
  totalAfterBase: Dec;
  totalBefore: Dec;
  totalAfter: Dec;
  /** Both endpoints are real locations, so nothing enters or leaves the company. */
  totalUnchanged: boolean;
  /** A resulting balance would be below zero. */
  wouldGoNegative: boolean;
  /** …and the warehouse (or C31, for a lot) forbids it: save is refused. */
  blocked: boolean;
  /** What the source still holds, in base units — the "en fazla" of the error message. */
  maxAvailableBase: Dec | null;
  maxAvailable: Dec | null;
}

/** What one (lot, location) pair holds. An untracked item sums every row at the location. */
export function balanceAt(balances: MovementBalanceRow[] | undefined, locationId: string | null | undefined, lotId: string | null | undefined): Dec {
  if (!locationId) return D(0);
  let total = D(0);
  for (const row of balances ?? []) {
    if (row.location_id !== locationId) continue;
    if (lotId && row.lot_id !== lotId) continue;
    total = total.plus(D(row.qty_base ?? 0));
  }
  return total;
}

function locationCode(balances: MovementBalanceRow[] | undefined, locationId: string): string | null {
  return (balances ?? []).find((r) => r.location_id === locationId && r.location_code)?.location_code ?? null;
}

/**
 * The "Sonuç önizleme" block: one line per affected balance (source first, as the
 * artboard draws it) and the total line. Computed from the rows the screen has
 * already loaded, so the numbers appear as the form is filled; the engine
 * recomputes them under its own lock at save time and is the authority.
 */
export function previewMovement(input: MovementPreviewInput): MovementPreview {
  const factor = input.unitFactor === null || input.unitFactor === undefined ? D(1) : D(input.unitFactor);
  const qtyBase = input.qtyBase === null || input.qtyBase === undefined ? null : D(input.qtyBase);
  const totalBeforeBase = D(input.itemTotalBase ?? 0);
  const lotLabel: string | null | 'new' = input.newLot ? 'new' : (input.lotNo ?? null);
  const sides: { locationId: string; direction: -1 | 1 }[] = [];
  if (input.fromLocationId) sides.push({ locationId: input.fromLocationId, direction: -1 });
  if (input.toLocationId) sides.push({ locationId: input.toLocationId, direction: 1 });

  const needsLot = !!input.isLotTracked;
  const hasLot = !!input.lotId || !!input.newLot;
  const shapeOk =
    sides.length > 0 &&
    (input.kind !== 'transfer' || sides.length === 2) &&
    (input.kind !== 'in' || !!input.toLocationId) &&
    (input.kind !== 'out' || !!input.fromLocationId);
  const complete = !!qtyBase && qtyBase.greaterThan(0) && shapeOk && (!needsLot || hasLot);

  const lines: MovementPreviewLine[] = sides.map((side) => {
    // a lot that does not exist yet has no balance anywhere
    const beforeBase = input.newLot ? D(0) : balanceAt(input.balances, side.locationId, input.lotId);
    const afterBase = qtyBase ? beforeBase.plus(qtyBase.times(side.direction)) : beforeBase;
    return {
      locationId: side.locationId,
      locationCode: locationCode(input.balances, side.locationId),
      lot: lotLabel,
      beforeBase,
      afterBase,
      before: inUnit(beforeBase, factor),
      after: inUnit(afterBase, factor),
      negative: afterBase.isNegative(),
      direction: side.direction,
    };
  });

  const delta = qtyBase ? qtyBase.times(sides.reduce((sum, s) => sum + s.direction, 0)) : D(0);
  const totalAfterBase = totalBeforeBase.plus(delta);
  const wouldGoNegative = lines.some((l) => l.negative);
  // mirrors private.movement_apply: the warehouse flag may allow a negative
  // location balance, but a lot balance never goes below zero (C31)
  const blocked = wouldGoNegative && (!input.allowNegativeAtSource || !!input.lotId || !!needsLot);
  const source = input.fromLocationId ? lines.find((l) => l.direction === -1) ?? null : null;

  return {
    complete,
    lines,
    totalBeforeBase,
    totalAfterBase,
    totalBefore: inUnit(totalBeforeBase, factor),
    totalAfter: inUnit(totalAfterBase, factor),
    totalUnchanged: sides.length === 2,
    wouldGoNegative,
    blocked,
    maxAvailableBase: source ? atLeastZero(source.beforeBase) : null,
    maxAvailable: source ? inUnit(atLeastZero(source.beforeBase), factor) : null,
  };
}

/** `greatest(x, 0)` — a source that is already negative offers nothing, not a negative maximum. */
function atLeastZero(value: Dec): Dec {
  return value.isNegative() ? D(0) : value;
}

/** Base quantity → the unit the form is entering in; a missing factor means 1:1. */
function inUnit(qtyBase: Dec, factor: Dec): Dec {
  return factor.greaterThan(0) ? qtyBase.dividedBy(factor) : qtyBase;
}

// ── undo window (C30) ──────────────────────────────────────────────────────
export interface UndoWindowSettings {
  /** `settings.stock.undo_seconds`, default 30. */
  undoSeconds?: number | string | null;
  /** `settings.stock.undo_grace_seconds`, default 15 — the server's tolerance, never advertised. */
  graceSeconds?: number | string | null;
}

export function undoSeconds(settings: UndoWindowSettings | null | undefined): number {
  const raw = Number(settings?.undoSeconds ?? 30);
  return Number.isFinite(raw) && raw > 0 ? Math.trunc(raw) : 30;
}

/**
 * May this row still be taken back with "Geri al"? The creator inside the window
 * undoes; after it, only a partner may post a reversal, and `reverse_movement`
 * refuses count, opening and document-bound rows outright.
 */
export function canUndoMovement(
  row: { created_by?: string | null; created_at?: string | null; reason?: StockMovementReason | null; ref_type?: string | null; reversed_by_id?: string | null; reversal_of_id?: string | null },
  ctx: { userId?: string | null; now?: Date | string | number; settings?: UndoWindowSettings | null },
): boolean {
  if (row.reversed_by_id || row.reversal_of_id) return false;
  if (row.reason === 'count' || row.reason === 'opening') return false;
  if (row.ref_type === 'goods_receipt' || row.ref_type === 'production_order') return false;
  if (!row.created_by || !ctx.userId || row.created_by !== ctx.userId || !row.created_at) return false;
  const elapsed = (new Date(ctx.now ?? new Date()).getTime() - new Date(row.created_at).getTime()) / 1000;
  return elapsed >= 0 && elapsed <= undoSeconds(ctx.settings);
}

/** A partner's reversal is offered instead, with the same refusals (§4.4 row click). */
export function canReverseMovement(
  row: { reason?: StockMovementReason | null; ref_type?: string | null; reversed_by_id?: string | null; reversal_of_id?: string | null },
  opts: { canApprove: boolean },
): boolean {
  if (!opts.canApprove) return false;
  if (row.reversed_by_id || row.reversal_of_id) return false;
  if (row.reason === 'count' || row.reason === 'opening') return false;
  return row.ref_type !== 'goods_receipt' && row.ref_type !== 'production_order';
}

// ── the payload (§4.5.7) ───────────────────────────────────────────────────
export interface NewLotInput {
  lotNo?: string | null;
  supplierId?: string | null;
  supplierLotNo?: string | null;
  expiryDate?: string | null;
  receivedAt?: string | null;
}

export interface PostMovementInput {
  companyId: string;
  /** Generated when the drawer opens, so a retry after a timeout never duplicates (C33). */
  clientUuid: string;
  kind: StockMovementKind;
  reasonCodeId: string;
  itemId: string;
  lotId?: string | null;
  newLot?: NewLotInput | null;
  fromLocationId?: string | null;
  toLocationId?: string | null;
  /** As entered, in `unit` — a decimal string, never a float. */
  qty: string;
  unit: string;
  note?: string | null;
  photoPaths?: string[];
  occurredAt?: string | null;
  /** G5: inbound unit cost in `currency` at `fxRate`; owner/partner only. */
  unitCost?: string | null;
  currency?: string | null;
  fxRate?: string | null;
  source?: 'web' | 'mobile';
}

/**
 * The jsonb `fn_post_stock_movement` expects. Empty strings are left out rather
 * than sent as null: `private.post_movement` reads every field with
 * `nullif(p ->> '…', '')`, so an absent key and an empty one mean the same, and a
 * smaller payload is easier to read in the audit log.
 */
export function movementPayload(input: PostMovementInput): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    company_id: input.companyId,
    client_uuid: input.clientUuid,
    kind: input.kind,
    reason_code_id: input.reasonCodeId,
    item_id: input.itemId,
    qty: input.qty,
    unit: input.unit,
    source: input.source ?? 'web',
  };
  if (input.lotId) payload.lot_id = input.lotId;
  if (input.fromLocationId) payload.from_location_id = input.fromLocationId;
  if (input.toLocationId) payload.to_location_id = input.toLocationId;
  if (input.note) payload.note = input.note;
  if (input.photoPaths?.length) payload.photo_paths = input.photoPaths;
  if (input.occurredAt) payload.occurred_at = input.occurredAt;
  if (input.unitCost) {
    payload.unit_cost = input.unitCost;
    if (input.currency) payload.currency = input.currency;
    if (input.fxRate) payload.fx_rate = input.fxRate;
  }
  if (input.newLot && !input.lotId) {
    const lot: Record<string, unknown> = {};
    if (input.newLot.lotNo) lot.lot_no = input.newLot.lotNo;
    if (input.newLot.supplierId) lot.supplier_id = input.newLot.supplierId;
    if (input.newLot.supplierLotNo) lot.supplier_lot_no = input.newLot.supplierLotNo;
    if (input.newLot.expiryDate) lot.expiry_date = input.newLot.expiryDate;
    if (input.newLot.receivedAt) lot.received_at = input.newLot.receivedAt;
    payload.new_lot = lot;
  }
  return payload;
}

/** What `fn_post_stock_movement` answers with (`private.movement_result`). */
export interface PostedMovement {
  id: string;
  no: string;
  kind: StockMovementKind;
  reason: StockMovementReason;
  item_id: string;
  lot_id: string | null;
  lot_no: string | null;
  qty: number;
  unit: string;
  qty_base: number;
  from_location_id: string | null;
  to_location_id: string | null;
  balances: { location_id: string; location_code: string | null; before_base: number; after_base: number }[];
  item_own_before_base: number;
  item_own_after_base: number;
  stock_status_after: string | null;
  warnings: { code: string; message: string }[];
  cost: { unit_cost_base: number | null; value_base: number | null; method: string | null; variance_base: number | null } | null;
  replay: boolean;
}
